<script lang="ts">
  import { t } from '$i18n/index';
  import type { MessageKey } from '$i18n/index';
  import {
    canPromptInstall,
    detectPlatform,
    isStandalone,
    promptInstall,
    watchForInstallPrompt,
  } from '$infra/install';
  import { focusOnMount } from '$ui/focus';

  interface Props {
    onDone: () => void;
    onLater: () => void;
  }

  const { onDone, onLater }: Props = $props();

  /**
   * The placement ritual — the keystone step, and the product's riskiest
   * assumption (spec §11, H1).
   *
   * Two things this screen does that are easy to get wrong:
   *
   * 1. **It states its own uncertainty.** The ritual is Tier 2 evidence
   *    (spec §4): plausible, inspired by cue-disruption work, demonstrated for
   *    nothing. The copy says we think it matters and that we are testing it,
   *    because claiming more would be exactly the overclaim §4 forbids.
   * 2. **It cannot actually do the thing it asks for.** Dragging an icon into a
   *    particular home-screen slot is not scriptable on any platform, and iOS
   *    has no install API at all. So this is instructions, honestly presented,
   *    with the native prompt offered only where one exists.
   */

  const platform = detectPlatform();
  let installable = $state(canPromptInstall());
  let installed = $state(isStandalone());

  $effect(() => watchForInstallPrompt((available) => (installable = available)));

  const stepKeys: Record<string, MessageKey[]> = {
    ios: ['install.ios.step1', 'install.ios.step2', 'install.ios.step3', 'install.ios.step4'],
    android: ['install.android.step1', 'install.android.step2', 'install.android.step3'],
    desktop: ['install.desktop.step1', 'install.desktop.step2'],
  };

  const titleKey = `install.${platform}.title` as MessageKey;
  const steps = $derived(stepKeys[platform] ?? []);

  async function install() {
    const accepted = await promptInstall();
    if (accepted) installed = true;
    installable = canPromptInstall();
  }
</script>

<h1 tabindex="-1" use:focusOnMount>{t('onboard.placement.title')}</h1>
<p class="body">{t('onboard.placement.body')}</p>
<p class="belief">{t('onboard.placement.belief')}</p>

<h2>{t(titleKey)}</h2>
<ol>
  {#each steps as key (key)}
    <li>{t(key)}</li>
  {/each}
</ol>

{#if installable && !installed}
  <button type="button" class="primary" onclick={() => void install()}>
    {t('onboard.placement.install')}
  </button>
{/if}

<div class="nav">
  <button type="button" class="back" onclick={onLater}>{t('onboard.placement.later')}</button>
  <button type="button" class="primary" onclick={onDone}>{t('onboard.placement.done')}</button>
</div>

<style>
  h1 {
    font-size: var(--step-2);
    font-weight: 400;
    line-height: 1.25;
  }

  h2 {
    font-size: var(--step-0);
    font-weight: 400;
    color: var(--c-ink-muted);
    margin-top: var(--space-3);
  }

  .body {
    font-size: var(--step-0);
  }

  .belief {
    font-size: var(--step--1);
    font-style: italic;
    color: var(--c-ink-muted);
  }

  ol {
    margin: 0;
    padding-left: var(--space-5);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    color: var(--c-ink-muted);
    font-size: var(--step--1);
  }

  .nav {
    display: flex;
    gap: var(--space-3);
    align-items: center;
    justify-content: space-between;
    margin-top: auto;
    padding-top: var(--space-4);
  }

  .primary {
    padding: var(--space-3) var(--space-5);
    border-radius: var(--radius-lg);
    border: 1px solid var(--c-accent-line);
    color: var(--c-accent);
    font-size: var(--step-0);
  }

  .back {
    color: var(--c-ink-faint);
    font-size: var(--step--1);
    padding: var(--space-2) var(--space-3);
  }
</style>
